import { TableCellProps } from "@mui/material";
import React, { useState } from "react";
import { OrderTableRow } from "../../../../pages/orders/my/order-list/styled";
import {
  LocationTableColumn,
  LocationTableColumnTypes,
} from "../model/location-table-column.model";
import BarekamText from "../../../../pages/components/text";
import BarekamButton, {
  SimpleButtons,
} from "../../../../pages/components/button";
import {
  BarekamIcon,
  IconVariants,
} from "../../../../pages/components/icon/barekam-icon";
import {
  BinLevel,
  CorridorLevel,
  DepotLevel,
  PartialOrderData,
  RackLevel,
  SectorLevel,
} from "../model/location-response.model";
import { fieldNamesByState } from "./locations-list";
import { LocationTableCell } from "../styled";
import {
  getLocationName,
  getTotalLocationCount,
  getTotalLocationPrice,
  getTotalLocationWeight,
} from "../../../../pages/shared/helper-functions";
import {
  Message,
  MessageSections,
} from "../../../../pages/components/message/message";
import { useAppSelector } from "../../../../redux/store";
import { OrderStatesEnum } from "../../../../pages/orders/enums/order-states.enum";
import { OrderModel } from "../../../../pages/shared/models/order/order-item.model";
import { LocationModel } from "../../../../pages/shared/models/location.model";
import ordersService from "../../../../services/orders.service";
import { InWarehouseActionTypes } from "../../../actions/action-types.enum";
import { RowActions } from "../../../../pages/orders/my/order-list/cell/blocks/row-actions";
import { RowActionsByState } from "../../../actions/actions-by-state";

export const LocationRow = (props: LocationTableRowProps) => {
  const [expanded, setExpanded] = useState(false);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const children = props.row[fieldNamesByState[props.level + 1]] || [];
  const orderActionsColumn: LocationTableColumn = {
    headerName: "actions",
    variant: LocationTableColumnTypes.ACTIONS,
  };

  return (
    <>
      <OrderTableRow hover tabIndex={props.index}>
        {!!children.length &&
          props.columns.map((column, columnIndex) => (
            <LocationTableCell
              width={
                column.variant === LocationTableColumnTypes.NAME
                  ? "55%"
                  : "unset"
              }
              style={{
                paddingLeft: `${columnIndex == 0 ? props.level + 1 : 1}rem`,
              }}
              key={`${props.index}-${columnIndex}-cell`}
            >
              <CellContent
                column={column}
                expanded={expanded}
                setExpanded={setExpanded}
                {...props}
              />
            </LocationTableCell>
          ))}
      </OrderTableRow>

      {props?.row?.orders &&
        props.row.orders.map((partialOrderData) => (
          <OrderTableRow key={`${partialOrderData.orderId}-row`}>
            <LocationTableCell
              width={"55%"}
              style={{ paddingLeft: `${props.level + 3}rem` }}
            >
              <BarekamButton
                type={SimpleButtons.TEXT}
                onClick={() => props.clicked(partialOrderData)}
              >
                {partialOrderData.internalTrackingNumber}
              </BarekamButton>
            </LocationTableCell>

            <LocationTableCell />
            <LocationTableCell />
            <LocationTableCell />

            <LocationTableCell>
              <CellContent
                column={orderActionsColumn}
                expanded={expanded}
                setExpanded={setExpanded}
                partialOrderData={partialOrderData}
                {...props}
              />
            </LocationTableCell>
          </OrderTableRow>
        ))}

      {expanded &&
        children &&
        children.map((child: any, index: number) => {
          const location = {} as LocationModel;

          location.depot = child.hasOwnProperty("depot")
            ? child.depot
            : props.location.depot || null;
          location.sector = child.hasOwnProperty("sector")
            ? child.sector
            : props.location.sector || null;
          location.corridor = child.hasOwnProperty("corridor")
            ? child.corridor
            : props.location.corridor || null;
          location.rack = child.hasOwnProperty("rack")
            ? child.rack
            : props.location.rack || null;
          location.bin = child.hasOwnProperty("bin")
            ? child.bin
            : props.location.bin || null;

          if (child.hasOwnProperty("sector")) {
            location.sector = child.sector;
          } else if (child.hasOwnProperty("corridor")) {
            location.corridor = child.corridor;
          } else if (child.hasOwnProperty("rack")) {
            location.rack = child.rack;
          } else if (child.hasOwnProperty("bin")) {
            location.bin = child.bin;
          }

          return (
            <LocationRow
              {...props}
              row={child}
              level={props.level + 1}
              key={index}
              location={location}
            />
          );
        })}
    </>
  );
};

const CellContent = ({
  column,
  row,
  level,
  expanded,
  setExpanded,
  onAction,
  onLocationRowAction,
  partialOrderData,
  location,
}: LocationTableCellProps) => {
  async function handleOnAction(row: OrderModel, actionKey: string) {
    if (partialOrderData) {
      ordersService.getOrderById(partialOrderData.orderId).then((orderData) => {
        orderData && onAction(orderData, actionKey);
      });
    }
  }

  switch (column.variant) {
    case LocationTableColumnTypes.NAME:
      return (
        <div style={{ display: "flex" }}>
          <BarekamButton
            type={SimpleButtons.TEXT}
            onClick={() => setExpanded((prevState) => !prevState)}
          >
            <BarekamIcon
              variant={IconVariants.arrow}
              rotation={expanded ? undefined : -90}
            />
          </BarekamButton>

          <BarekamText variant={"m"}>{getLocationName(row)}</BarekamText>
        </div>
      );
    case LocationTableColumnTypes.TOTAL_PAYMENTS:
      const [rowPrice] = useState(getTotalLocationPrice(row, 0));

      return (
        <BarekamText variant={"m"}>
          {rowPrice}{" "}
          <Message messageKey={"amd"} sectionId={MessageSections.MAIN} />
        </BarekamText>
      );
    case LocationTableColumnTypes.TOTAL_WEIGHT:
      const [rowWeight] = useState(getTotalLocationWeight(row, 0));

      return (
        <BarekamText variant={"m"}>
          {rowWeight}{" "}
          <Message messageKey={"kg"} sectionId={MessageSections.MAIN} />
        </BarekamText>
      );
    case LocationTableColumnTypes.TOTAL_ORDERS_COUNT:
      const [rowCount] = useState(getTotalLocationCount(row, 0));

      return <BarekamText variant={"m"}>{rowCount}</BarekamText>;
    case LocationTableColumnTypes.ADD_ORDER:
      return (
        <div style={{ width: "100%", display: "flex", justifyContent: "end" }}>
          <BarekamButton
            type={SimpleButtons.TEXT}
            onClick={() =>
              onLocationRowAction(
                location,
                InWarehouseActionTypes.PRE_SELECT_LOCATION_AND_ADD_ORDERS,
              )
            }
          >
            <BarekamText variant={"xxl"} color={"#919191"} fontWeight={500}>
              +
            </BarekamText>
          </BarekamButton>
        </div>
      );
    case LocationTableColumnTypes.ACTIONS:
      const { orderStateId } = useAppSelector((state) => state.ordersReducer);
      switch (orderStateId) {
        case OrderStatesEnum.inWarehouse:
          return (
            <RowActions
              onAction={handleOnAction}
              actions={RowActionsByState[OrderStatesEnum.inWarehouse]}
            />
          );
        case OrderStatesEnum.readyForShipment:
          return (
            <RowActions
              onAction={handleOnAction}
              actions={RowActionsByState[OrderStatesEnum.readyForShipment]}
            />
          );
        case OrderStatesEnum.onTheWay:
          return (
            <RowActions
              onAction={handleOnAction}
              actions={RowActionsByState[OrderStatesEnum.onTheWay]}
            />
          );
        case OrderStatesEnum.unidentified:
          return (
            <RowActions
              onAction={handleOnAction}
              actions={RowActionsByState[OrderStatesEnum.unidentified]}
            />
          );
        case OrderStatesEnum.readyForPickup:
          return (
            <RowActions
              onAction={handleOnAction}
              actions={RowActionsByState[OrderStatesEnum.readyForPickup]}
            />
          );
        case OrderStatesEnum.inLocalWarehouse:
          return (
            <RowActions
              onAction={handleOnAction}
              actions={RowActionsByState[OrderStatesEnum.inLocalWarehouse]}
            />
          );
        default:
          return <></>;
      }
    default:
      return <></>;
  }
};

interface LocationTableRowProps extends TableCellProps {
  columns: LocationTableColumn[];
  location: LocationModel;
  row: DepotLevel | SectorLevel | CorridorLevel | RackLevel | BinLevel;
  level: number;
  clicked: (data: PartialOrderData) => void;
  onAction: (row: OrderModel, action: string) => void;
  onLocationRowAction: (location: LocationModel, action: string) => void;
  index: number;
}

interface LocationTableCellProps extends LocationTableRowProps {
  column: LocationTableColumn;
  expanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  partialOrderData?: PartialOrderData;
  onAction: (row: OrderModel, action: string) => void;
  onLocationRowAction: (location: LocationModel, action: string) => void;
}
